import { useNavigate } from "react-router-dom";
import { Sparkles, Plus, Wine } from "lucide-react";

/**
 * EmptyDashboard — first-run empty state for `/dashboard` when the user
 * has zero collections. Warm, guided welcome with a single primary CTA
 * that routes into the existing `/collections/builder` guided wizard.
 *
 * Kept intentionally lightweight: no new UI primitives, no data fetches,
 * no state — the parent decides when to mount it.
 *
 * Styling notes
 * -------------
 * • `vp-card` container reuses the app-wide surface treatment so this
 *   panel matches the rest of the dashboard's visual rhythm.
 * • Icon column uses the CDI gold accent — same treatment as the other
 *   EmptyState variants across the app.
 * • Layout stacks vertically on mobile (icon → title → subtitle → CTA)
 *   and stays vertically centred in the parent viewport.
 */
export default function EmptyDashboard() {
  const navigate = useNavigate();
  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-20"
      data-testid="dashboard-empty"
    >
      <div className="vp-card p-6 sm:p-10 text-center vp-fade-in">
        <div
          className="mx-auto mb-6 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center"
          aria-hidden="true"
        >
          <Sparkles size={32} strokeWidth={1.75} className="hidden sm:inline" />
          <Wine size={26} strokeWidth={1.75} className="sm:hidden" />
        </div>

        <h1
          className="text-xl sm:text-2xl font-semibold tracking-tight mb-2"
          data-testid="dashboard-empty-title"
        >
          Welcome to CDI Collectables
        </h1>
        <p className="text-sm vp-muted max-w-md mx-auto leading-relaxed mb-6">
          Start by creating a collection, then organise it into categories,
          and finally log each collectable as an item.
        </p>

        <button
          type="button"
          onClick={() => navigate("/collections/create")}
          data-testid="dashboard-empty-cta"
          className="vp-btn vp-btn-primary inline-flex items-center gap-2"
        >
          <Plus size={15} />
          Create Collection
        </button>

        <p className="text-[11px] vp-muted mt-6">
          Your dashboard will populate automatically as you add items.
        </p>
      </div>
    </div>
  );
}
